// src/utils/adminHelpers.js
// Centralised admin-tier helpers.
// Platform superadmin: explicit users.is_sys_admin grant (mirrors is_admin()/is_platform_admin() RPCs).
// Locality admin: scoped per boundary (mirrors is_locality_admin() RPC).
// Steward: directory/listings duty (mirrors is_steward() RPC).

import { supabaseFetch } from '../lib/supabase';

/**
 * Check if a user object represents a platform superadmin.
 * Mirrors the is_admin(user_id)/is_platform_admin() RPCs, both of which check
 * users.is_sys_admin (an explicit grant, not an email-domain pattern match —
 * see docs/briefs/2026-08-15-support-delivery-per-channel-markers.md).
 * @param {Object|null} user - User object with an `is_sys_admin` property
 * @returns {boolean}
 */
export function isPlatformAdmin(user) {
  return Boolean(user?.is_sys_admin === true);
}

/**
 * Check if a user is a locality admin for a specific boundary (any role).
 * Calls the is_locality_admin(p_boundary, p_user) RPC.
 * @param {string} userId - UUID of the user
 * @param {number} boundaryId - admin_boundaries.id
 * @returns {Promise<boolean>}
 */
export async function isLocalityAdmin(userId, boundaryId) {
  try {
    const { data, error } = await supabaseFetch.rpc('is_locality_admin', {
      p_boundary: boundaryId,
      p_user: userId
    });
    if (error) {
      console.error('isLocalityAdmin check failed:', error);
      return false;
    }
    return Boolean(data);
  } catch (err) {
    console.error('isLocalityAdmin exception:', err);
    return false;
  }
}

/**
 * Check if a user is a Steward for a specific boundary.
 * Calls the is_steward(p_boundary, p_user) RPC.
 * @param {string} userId - UUID of the user
 * @param {number} boundaryId - admin_boundaries.id
 * @returns {Promise<boolean>}
 */
export async function isSteward(userId, boundaryId) {
  try {
    const { data, error } = await supabaseFetch.rpc('is_steward', {
      p_boundary: boundaryId,
      p_user: userId
    });
    if (error) {
      console.error('isSteward check failed:', error);
      return false;
    }
    return Boolean(data);
  } catch (err) {
    console.error('isSteward exception:', err);
    return false;
  }
}

/**
 * Get all locality admin assignments for a user, with role.
 * @param {string} userId - UUID of the user
 * @param {string} [role] - Optional role filter ('steward', 'peacemaker')
 * @returns {Promise<Array<{boundary_id: number, role: string}>>}
 */
export async function getUserLocalityAdminBoundaries(userId, role) {
  try {
    const filter = { user_id: userId, is_active: true };
    if (role) filter.role = role;

    const { data, error } = await supabaseFetch.query('locality_admins', {
      select: 'boundary_id,role',
      eq: filter
    });
    if (error || !data) return [];
    return data.map(row => ({ boundary_id: row.boundary_id, role: row.role }));
  } catch (err) {
    console.error('getUserLocalityAdminBoundaries exception:', err);
    return [];
  }
}
